import React from 'react';
import debounce from 'lodash/debounce';
import { connect } from 'react-redux';
import { Popover, SearchField } from '@drone-js/core';
import { mapStateToProps, SearchBoxPopoverProps } from 'component/common/SearchBox/SearchBoxPopover.selectors';
import TextSearchDropdown from 'component/common/TextSearch/TextSearchDropdown';
import { useLazyGetSuggestResultQuery } from 'store/api/activitiesApi';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesHeaderSearchBarTextInputClickEventActivitiesFieldsV2,
    ActivitiesHeaderSearchBarTextInputChangeEventActivitiesFieldsV2,
    ActivitiesLandingTextInputClickEventFieldsV2,
    ActivitiesLandingTextInputChangeEventActivitiesFieldsV1,
    ActivitiesGlobalKeywordSearchSeeAllResultKeyPressEventActivitiesFieldsV1,
    ActivitiesInCitySearchSeeAllResultKeyPressEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesGlobalKeywordSearchSeeAllResultKeyPressEventActivitiesManifestV1,
    ActivitiesHeaderSearchBarTextInputChangeEventActivitiesManifestV2,
    ActivitiesHeaderSearchBarTextInputClickEventActivitiesManifestV2,
    ActivitiesInCitySearchSeeAllResultKeyPressEventActivitiesManifestV1,
    ActivitiesLandingTextInputChangeEventActivitiesManifestV1,
    ActivitiesLandingTextInputClickEventManifestV2,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { LeanScrollbarBox } from 'component/common/LeanScrollBarBox.styled';
import { referrer } from 'core-framework/window/document';
import { isHomePage } from 'core/constant/analytics/AnalyticsPageType';
import { getItemFromSessionStorage, setItemInSessionStorage } from 'core-framework/window/storage';
import { SessionStorageKey } from 'core-framework/constant/StorageKeys';
import { useLocation } from 'react-router-dom';
import { getCityIdQueryParams } from 'gql/service/graph/activityService';

const CITY_TERM = 'city';

const PopOverContent: React.FC<React.PropsWithChildren<{ isHomeSearchBox: boolean; isNewHomePageEnabled?: boolean }>> = (props) => {
    const popoverWidth = props.isHomeSearchBox ? 'calc(744px - 16*2px - 4*2px)' : 'calc(744px - 16*2px)';
    const elementName = 'activities-landing-popover-suggestion';
    // NO_TEXT_SEARCH_SCROLL: maxHeight=600px to avoid showing scrollbar in the popover
    return (
        <LeanScrollbarBox
            width={popoverWidth}
            maxHeight={props.isNewHomePageEnabled ? '450px' : '600px'}
            overflowY="auto"
            overflowX="hidden"
            data-testid={elementName}
            id={elementName}
        >
            {props.children}
        </LeanScrollbarBox>
    );
};
PopOverContent.displayName = 'PopOverContent';

const SearchBoxPopover: React.FC<SearchBoxPopoverProps> = (props) => {
    const { cms, query, setSearchText, setSelectedResult, onClearSearch, isSearchBoxFeatureGroup } = props;
    const searchFieldRef = React.useRef<HTMLInputElement>(null);
    const [showPopover, setShowPopover] = React.useState<boolean>(false);
    const [inputText, setInputText] = React.useState<string>(query);
    const [triggerGetSuggestResultQuery] = useLazyGetSuggestResultQuery();
    const location = useLocation();

    React.useEffect(() => {
        setInputText(query);
    }, [query]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedTriggerGetSuggestResultQuery = React.useCallback(
        debounce((text: string) => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            triggerGetSuggestResultQuery(text);
            props.isHomeSearchBox
                ? trackElementEvent<ActivitiesLandingTextInputChangeEventActivitiesFieldsV1>(
                      ActivitiesLandingTextInputChangeEventActivitiesManifestV1.ids,
                      {},
                      { action_element_value: text },
                  )
                : trackElementEvent<ActivitiesHeaderSearchBarTextInputChangeEventActivitiesFieldsV2>(
                      ActivitiesHeaderSearchBarTextInputChangeEventActivitiesManifestV2.ids,
                      {},
                      { action_element_value: text },
                  );
        }, 500),
        [triggerGetSuggestResultQuery],
    );

    React.useEffect(() => {
        const queryText = inputText === '' ? (isSearchBoxFeatureGroup ? inputText : CITY_TERM) : inputText;
        if (queryText && queryText.length >= 1) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            debouncedTriggerGetSuggestResultQuery(queryText);
        }
    }, [inputText, debouncedTriggerGetSuggestResultQuery, isSearchBoxFeatureGroup]);

    React.useEffect(() => {
        const searchInput = searchFieldRef?.current;
        if (!searchInput) return;

        // TODO: Remove custom aria attributes when SearchField component supports them natively
        searchInput.setAttribute('autocomplete', 'off');
        searchInput.setAttribute('aria-controls', 'activities-landing-popover-suggestion');
        searchInput.setAttribute('aria-expanded', 'false');
    }, []);

    React.useEffect(() => {
        const searchInput = searchFieldRef?.current?.querySelector('input');
        if (!searchInput) return;
        searchInput.setAttribute('aria-expanded', showPopover.toString());
    }, [showPopover]);

    React.useEffect(() => {
        const referrerUrl = referrer();
        const shouldShowSearchOverlay =
            getItemFromSessionStorage(SessionStorageKey.SEARCH_OVERLAY) !== 'true' &&
            isHomePage(props.pageTypeId) &&
            props.isOpenSearchOverlayOnLanding &&
            !referrerUrl.includes('/activities');
        if (shouldShowSearchOverlay) {
            setShowPopover(true);
            searchFieldRef.current?.focus();
            setItemInSessionStorage(SessionStorageKey.SEARCH_OVERLAY, 'true');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const closePopover = () => {
        setShowPopover(false);
    };

    const openPopover = () => {
        if (!showPopover) {
            setShowPopover(true);
            props.isHomeSearchBox
                ? trackElementEvent<ActivitiesLandingTextInputClickEventFieldsV2>(ActivitiesLandingTextInputClickEventManifestV2.ids, {})
                : trackElementEvent<ActivitiesHeaderSearchBarTextInputClickEventActivitiesFieldsV2>(ActivitiesHeaderSearchBarTextInputClickEventActivitiesManifestV2.ids, {});
        }
    };

    const performGeoSearch = (text: string) => {
        trackElementEvent<ActivitiesInCitySearchSeeAllResultKeyPressEventActivitiesFieldsV1>(ActivitiesInCitySearchSeeAllResultKeyPressEventActivitiesManifestV1.ids, {
            city_id: props.geo?.cityId || -1,
        });
        props.geo && props.onKeywordResultSelect && props.onKeywordResultSelect(text, props.geo);
    };

    const performGlobalKeywordSearch = (text: string) => {
        trackElementEvent<ActivitiesGlobalKeywordSearchSeeAllResultKeyPressEventActivitiesFieldsV1>(
            ActivitiesGlobalKeywordSearchSeeAllResultKeyPressEventActivitiesManifestV1.ids,
            { search_phrase: text },
        );

        props.onGlobalKeywordResultSelect && props.onGlobalKeywordResultSelect(text);
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            closePopover();
        }
        if (props.isGlobalKeywordSearch && e.key === 'Enter' && inputText !== '') {
            const cityId = getCityIdQueryParams(location.search);
            if (props.isModifyEnterKeyPressForSearchExpOn) {
                // isDisabledGlobalKeywordSearch is true for city page only, so either its city page or ssr with city id, we will perform geo search
                if ((props.isDisabledGlobalKeywordSearch || cityId) && props.geo && props.onKeywordResultSelect) {
                    performGeoSearch(inputText);
                } else if (props.onGlobalKeywordResultSelect) {
                    performGlobalKeywordSearch(inputText);
                }
            } else if (props.onKeywordResultSelect && props.isDisabledGlobalKeywordSearch && props.geo) {
                performGeoSearch(inputText);
            } else {
                performGlobalKeywordSearch(inputText);
            }
        }

        if (e.key === 'Enter' || (e.key.length === 1 && /[A-Z0-9]/.test(e.key))) {
            openPopover();
        }
    };

    const handleSelectedResult = (text: string) => {
        setSearchText(text);
        setInputText(text);
        openPopover();
    };

    const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const text = e.currentTarget.value;
        handleSelectedResult(text);
        if (isSearchBoxFeatureGroup) {
            if (text !== '') {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                debouncedTriggerGetSuggestResultQuery(text);
            }
        } else {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            debouncedTriggerGetSuggestResultQuery(text || CITY_TERM);
        }
    };

    return (
        <Popover
            pointerPosition="start-above"
            content={
                <PopOverContent isHomeSearchBox={props.isHomeSearchBox} isNewHomePageEnabled={props.isNewHomePageEnabled}>
                    <TextSearchDropdown
                        inputText={inputText}
                        handleSelectedResult={handleSelectedResult}
                        setSelectedResult={setSelectedResult}
                        onClose={closePopover}
                        popularActivities={props.popularActivities}
                    />
                </PopOverContent>
            }
            fixed={true}
            visible={showPopover}
            onBlur={closePopover}
            flexWrapper={{
                flex: 1,
            }}
            autoHeightResize={false}
            p="m"
        >
            <SearchField
                ref={searchFieldRef}
                data-testid={props.dataElementName}
                data-element-name={props.dataElementName}
                variant="input"
                placeholderText={props.placeholderText}
                inputText={inputText}
                onChange={handleQueryChange}
                leadingAsset={{
                    id: 'fill.symbol.search',
                }}
                label={cms[238402]}
                clearLabel={cms[264787]}
                onClick={openPopover}
                onKeyDown={onKeyDown}
                onClear={onClearSearch}
                role="combobox"
                aria-haspopup="listbox"
                aria-autocomplete="list"
                aria-controls="activities-overlay-text-search-result-list"
                aria-expanded={showPopover}
            />
        </Popover>
    );
};

SearchBoxPopover.displayName = 'SearchBoxPopover';

export default connect(mapStateToProps)(SearchBoxPopover);
